import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { thoughtSpotInit, thoughtSpotLogout } from './modules/App';

// MUI X License
import { LicenseInfo } from '@mui/x-license';

import FourZeroFourPage from 'components/FourZeroFourPage';
import ForbiddenPage from 'components/ForbiddenPage';
import NotificationsProvider from 'components/NotificationsProvider';
import ProgressBar from 'components/ProgressBar';
import RouteLoader from 'components/RouteLoader';
import useGoogleAnalytics from 'components/WithGoogleAnalytics';

import * as styles from './App.scss';

// Set MUI X license key
// It's understood that this will be exposed in the client.
// See https://mui.com/x/introduction/licensing/#license-key-security
LicenseInfo.setLicenseKey('1a54d71c936ee378fa2ee1f6d66555b0Tz0xMTk2NjgsRT0xNzkwNDY3MTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=');

/* Route Lazy Imports */
const BrandSwitcher = lazy(() => import('./routes/BrandSwitcher/containers/BrandSwitcherContainer'));
const Login = lazy(() => import('./routes/Auth/components/Login'));
const Home = lazy(() => import('./routes/Home/containers/HomeContainer'));
const Admin = lazy(() => import('./routes/Admin/containers/AdminContainer'));
const SnowflakeIntegrationsPublic = lazy(() => import('./routes/SnowflakeIntegrationsPublic/components/SnowflakeIntegrationsPublic'));

/**
 * @function App
 * @description Main App Component for Bridg-CDP Project
 */
const App = (props) => {
  const {
    isAuthenticated,
    isBridgAdmin,
  } = props;

  // Redux state
  const dispatch = useDispatch();
  const account = useSelector((state) => state.app.account);
  const brand = useSelector((state) => state.app.brand);
  const dataLoading = useSelector((state) => state.app.dataLoading);
  const thoughtSpotInitialized = useSelector((state) => state.app.thoughtSpotInitialized);
  const thoughtSpotSession = useSelector((state) => state.app.thoughtSpotSession);

  const { enableAdministrationPanel, enableSnowflakeIntegrationsRoute, requiresThoughtspot } = useFlags();

  // Initialize Google Analytics
  useGoogleAnalytics();

  // Track previous props for comparison in useEffect
  const prevPropsRef = useRef();

  // Handle ThoughtSpot initialization and cleanup (replaces componentDidUpdate)
  useEffect(() => {
    const prevProps = prevPropsRef.current;

    // Skip on initial mount
    if (prevProps) {
      const handleThoughtSpotUpdate = async () => {
        if (requiresThoughtspot) {
          if (!thoughtSpotInitialized && brand.id) {
            await dispatch(thoughtSpotInit(brand));
          } else if (brand.id && (brand.id !== prevProps?.brand?.id)) {
            await dispatch(thoughtSpotLogout());
          } else if (!brand.id && prevProps.brand.id && thoughtSpotInitialized && thoughtSpotSession) {
            await dispatch(thoughtSpotLogout());
          }
        }
      };

      handleThoughtSpotUpdate();
    }

    // Update previous props ref after effect runs
    // Add additional props as needed
    prevPropsRef.current = { brand };
  }, [brand, dispatch, requiresThoughtspot, thoughtSpotInitialized, thoughtSpotSession]);

  return (
    <NotificationsProvider>
      <section className={styles.appContainer}>
        <ProgressBar />
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/403" element={<ForbiddenPage />} />
            {account.sid && brand.sid && isAuthenticated && (
              <Route path="/:accountSid/:brandSid/*" element={<Home />} />
            )}
            {isAuthenticated && (
              <Route path="/" element={<BrandSwitcher />} />
            )}
            {isAuthenticated && isBridgAdmin && enableAdministrationPanel && (
              <Route path="/administration/*" element={<Admin />} />
            )}
            {isAuthenticated && enableSnowflakeIntegrationsRoute && (
              <Route path="/snowflake-integrations" element={<SnowflakeIntegrationsPublic />} />
            )}
            {!dataLoading && (
              <Route path="*" element={<FourZeroFourPage {...props} />} />
            )}
          </Routes>
        </Suspense>
      </section>
    </NotificationsProvider>
  );
};

export default App;
